// src/try/PartIntro.tsx: on the public test ride, a short card the first time the visitor reaches each part of the tour
// (owner, 2026-10-23: the landing dropped them on Harbor & Pine's form "without any indication or helping them
// understand what's happening"). It says who they are now and what to do, with the three parts marked as on the bar.
// Once per part for the visitor's copy (local storage, keyed by the copy); never on the private demo.
import { useState } from "preact/hooks";
import { route } from "../app/router";
import { barModel, CHAPTERS, tour, tourRoom } from "../demo/tour";
import { local } from "../lib/storage";
import { me, site } from "../state/session";
import { Button } from "../ui/Button";
import { cx } from "../ui/cx";
import { Dialog } from "../ui/Sheet";
import "../styles/try.css";

type Part = 1 | 2 | 3;

export const INTROS_KEY = "try:intros";

/** What each part's card says. */
export const PART_INTROS: Record<Part, { title: string; body: string; cta: string }> = {
  1: {
    title: "First, You're the Family",
    body: "You found Harbor & Pine Homes online and pressed Start Your Build. Answer like a buyer would, then plan your home on the board it opens.",
    cta: "Start as the Family",
  },
  2: {
    title: "Now You're the Builder",
    body: "This is Harbor & Pine's workspace. The family you just played is here, with a brief written for your first call. Reply, or suggest times.",
    cta: "See Your Team's Side",
  },
  3: {
    title: "Your Business",
    body: "A family weeks into planning, then your Pipeline: who's serious, who's waiting on you, who's gone quiet.",
    cta: "Continue",
  },
};

/** This tab's own record, so a closed card stays closed where storage is blocked. */
const shown = new Map<string, Set<Part>>();

/** The parts this copy's visitor has been shown, or none for another copy. */
export function seenParts(copy: string): Part[] {
  const v = local.getJSON<{ copy?: unknown; seen?: unknown } | null>(INTROS_KEY, null);
  const stored = v && v.copy === copy && Array.isArray(v.seen) ? v.seen.filter((n): n is Part => n === 1 || n === 2 || n === 3) : [];
  const mine = shown.get(copy) ?? new Set<Part>();
  return ([1, 2, 3] as const).filter((n) => stored.includes(n) || mine.has(n));
}

function markSeen(copy: string, part: Part): void {
  const seen = seenParts(copy);
  shown.set(copy, new Set([...seen, part]));
  if (!seen.includes(part)) local.setJSON(INTROS_KEY, { copy, seen: [...seen, part] });
}

/** Tests: forget this tab's record. */
export function resetPartIntros(): void {
  shown.clear();
}

export function PartIntro() {
  const copy = site.value?.trial?.key ?? null;
  const model = barModel(route.value, tour.value, tourRoom.value, !!me.value?.staff);
  const [, redraw] = useState(0);
  if (!copy || model.chapter === 0) return null;
  const part = model.chapter as Part;
  const open = !seenParts(copy).includes(part);
  const close = () => {
    markSeen(copy, part);
    redraw((n) => n + 1);
  };
  const intro = PART_INTROS[part];
  return (
    <Dialog open={open} onClose={close} eyebrow={`Test Ride · Part ${part} of 3`} title={intro.title} class="try-intro-card" footer={<Button iconAfter="arrow-r" onClick={close}>{intro.cta}</Button>}>
      <ol class="pi-parts" aria-label="The test ride">
        {CHAPTERS.map((c) => (
          <li key={c.n} class={cx(c.n === part && "is-here", c.n < part && "is-done")} aria-current={c.n === part ? "step" : undefined}>
            <span class="pi-n">{c.n}</span>
            <span class="pi-long">{c.long}</span>
            <span class="pi-short">{c.short}</span>
          </li>
        ))}
      </ol>
      <p class="pi-body">{intro.body}</p>
      {part === 1 ? <p class="pi-note">The bar at the top shows where you are, and what's next.</p> : null}
    </Dialog>
  );
}
